// 3-card tarot reading — homepage section.
//
// Flow: pick 3 of 78 face-down -> cards flip with their free one-line meanings
// -> "Get free reading" -> sign in -> the LLM interprets the SAME three cards.
//
// The cards flip and give a real (static, zero-cost) meaning BEFORE any login.
// That is the point: the visitor gets something genuinely theirs, which is what
// makes them want the interpretation. Only the interpretation is gated.
//
// THE FIRST READING IS FREE, ONCE. After that a reading costs what a chat
// question costs, and — the part this file has to get right — buying one NEVER
// replaces an earlier one. Every reading is its own row server-side, and
// "Past readings" below shows them, so the visitor can see that spending again
// adds rather than overwrites. Nobody buys a second reading if they think it
// costs them the first.
//
// Money rule enforced on BOTH sides: nothing is charged without an explicit
// confirmation the visitor clicked. The server ignores a request without
// confirm_paid and answers with a price instead, which is what stops the
// post-login draw replay from silently billing a returning user.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconSparkles,
  IconStar,
  IconMessageCircle2,
  IconRefresh,
  IconChevronRight,
  IconHistory,
} from "@tabler/icons-react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";
import RechargeSheet from "./RechargeSheet";
import {
  TAROT_DECK,
  CARD_BY_ID,
  POSITIONS,
  type DrawnCard,
} from "../data/tarotDeck";
import {
  savePendingDraw,
  takePendingDraw,
  clearPendingDraw,
  newDrawId,
} from "../hooks/useTarotDraw";
import styles from "./TarotSection.module.css";
import { isPrerender } from "../utils/isPrerender";

// Shown to non-Indian visitors only, matching the guest-chat funnel (Arjun,
// 2026-11-03: no tarot in India). Flip to true to open it to everyone.
const SHOW_FOR_ALL_COUNTRIES = false;

// react-snap prerenders the homepage in a headless browser. Keep the static
// shell (good for SEO: "free tarot reading" is high-volume) but never let
// client-only state be baked into the snapshot.


// Rotating status lines. Each names something the reader is actually doing, so
// the wait reads as work rather than as a stall.
const LOADING_STEPS = [
  "Turning your cards…",
  "Reading the spread as one story…",
  "Weighing what sits in each position…",
  "Writing your reading…",
];

interface Reading {
  past: string;
  present: string;
  future: string;
  summary: string;
}

/** What another reading costs this user right now, priced by the server. */
interface Offer {
  cost_minor: number;
  currency: string;
  affordable: boolean;
}

// Wallet amounts cross the wire in MINOR units (paise-coins, ₹1 = 100), the same
// unit the wallet RPCs use. Converting at the edge keeps that unit out of the
// rest of this file, where mixing it with the major-unit service_prices row
// would silently show a price 100x wrong.
const minorToMajor = (minor: number) => minor / 100;

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$", GBP: "£", EUR: "€", INR: "₹", AED: "AED ",
};
function fmtPrice(amount: number, currency: string | null) {
  const c = (currency || "USD").toUpperCase();
  const sym = CURRENCY_SYMBOL[c] ?? `${c} `;
  // Whole numbers read better on a CTA than trailing zeros.
  const n = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${sym}${n}`;
}

interface Props {
  /** Opens the site's AuthModal. Owned by HomePage. */
  onRequestLogin: () => void;
  /**
   * Skip the non-India gate. The gate is correct on the HOMEPAGE, where tarot
   * is an extra shown to foreign visitors. It is wrong on a page that exists
   * to sell the draw: /usa and /uae would otherwise render a tarot landing
   * page with no tarot on it for anyone in India, including us looking at our
   * own page.
   */
  forceVisible?: boolean;
}

export default function TarotSection({
  onRequestLogin,
  forceVisible = false,
}: Props) {
  const { user, userCountry, geoChecked, currency, pricingVariant, refreshUserStatus } = useAuth();
  const navigate = useNavigate();

  const [picked, setPicked] = useState<DrawnCard[]>([]);
  // No question field in the UI any more (owner call). The state stays because
  // the API still accepts a question and a previously-saved reading can carry
  // one — dropping it would silently lose that text on replay.
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState<Reading | null>(null);
  const [readCards, setReadCards] = useState<DrawnCard[] | null>(null);
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadStep, setLoadStep] = useState(0);
  // What a further reading costs. Further readings happen through chat, so the
  // price IS the chat-message price for this user's currency/variant.
  const [chatPrice, setChatPrice] = useState<number | null>(null);
  const [checkedExisting, setCheckedExisting] = useState(false);
  // A scroll REQUEST, not the scroll itself. See the effect below for why the
  // two cannot be the same thing.
  const [scrollWanted, setScrollWanted] = useState(false);
  // True when the reading on screen is one this account generated earlier, and
  // the visitor has just picked a different spread expecting a new one.
  const [replacedPick, setReplacedPick] = useState(false);

  // ---- paid re-reads ----
  // The server prices each further reading (membership + variant aware), so this
  // is authoritative and `chatPrice` is only the fallback for when it is absent.
  const [offer, setOffer] = useState<Offer | null>(null);
  // How many readings this account has. Drives the "Past readings (n)" affordance
  // that makes buying another feel additive rather than destructive.
  const [count, setCount] = useState(0);
  // Non-null while a spend is waiting on an explicit confirmation click. Holds
  // the exact cards being bought so a re-render cannot swap them underneath.
  const [pendingBuy, setPendingBuy] = useState<DrawnCard[] | null>(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  // Identity of the CURRENT spread, and the server's double-charge guard: it
  // charges at most once per (user, draw_id). Minted the moment the third card
  // lands and then held still through every retry, the login round trip and the
  // confirm step. Cleared only when the spread itself changes, because that is
  // the only thing that makes it a different draw.
  const [drawId, setDrawId] = useState<string | null>(null);

  // Guards the auto-resume after login against StrictMode's double effect.
  const resumedRef = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  // The spread is rows of overlapping cards. 39 per row fits the 1040px desktop
  // column exactly, but on a phone that same row is ~1050px wide, so the deck
  // sat behind a horizontal scroller with most of it off screen. Fewer cards
  // per row means more rows, and the whole deck fits the viewport with the same
  // spread look and no scrolling.
  const [perRow, setPerRow] = useState(39);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setPerRow(mq.matches ? 13 : 39);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  const deckRowCount = Math.ceil(TAROT_DECK.length / perRow);

  // "Is this a foreign visitor?" has TWO different answers depending on auth,
  // and using the wrong one hides the section from the very people it is for.
  //
  //   logged OUT -> userCountry, resolved by the Geoapify probe.
  //   logged IN  -> currency (users.currency_code). AuthContext runs
  //                 detectGuestLocation() ONLY for guests and just does
  //                 setGeoChecked(true) for a session, so userCountry stays at
  //                 its 'IN' default forever once you sign in. Gating on it
  //                 made the section vanish the moment a US user logged in —
  //                 which is exactly when we need it.
  //
  // Both sides wait for their signal to resolve rather than guessing, so an
  // Indian visitor never sees it flash in and disappear.
  const isForeign = user
    ? !!currency && currency.toUpperCase() !== "INR"
    : geoChecked && !!userCountry && userCountry.toUpperCase() !== "IN";
  const visible =
    !isPrerender() && (forceVisible || SHOW_FOR_ALL_COUNTRIES || isForeign);

  const submit = useCallback(
    async (cards: DrawnCard[], q: string, confirmPaid = false, draw?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const payload = cards.map((c) => ({
          id: c.id,
          reversed: c.reversed,
          name: CARD_BY_ID[c.id]?.name ?? c.id,
        }));
        // Goes to the GO BACKEND, not a Supabase edge function.
        // REACT_APP_SUPABASE_URL is https://api.astroaura.ai (Caddy -> Go), so
        // functions.invoke() posts to /functions/v1/tarot-reading there and the
        // Supabase session JWT is verified by the Go verifier (HS256). Kept as
        // invoke() rather than raw fetch so auth headers and errors behave
        // exactly like every other call in the app.
        const { data, error: fnErr } = await supabase.functions.invoke("tarot-reading", {
          // draw_id is passed explicitly rather than read from state: submit is
          // called from the login-resume effect with the draw it just took out
          // of sessionStorage, and at that moment the state has not caught up.
          // Reading state here would send a stale id, or none, and quietly
          // reopen the double-charge window.
          body: {
            cards: payload,
            question: q,
            confirm_paid: confirmPaid,
            draw_id: draw ?? undefined,
          },
        });
        if (fnErr) {
          // A 402 is not a failure, it is a price. supabase-js throws on any
          // non-2xx and parks the Response on `.context`, so the body has to be
          // read back out or the user just sees "Edge Function returned a
          // non-2xx status code" and has no idea they need balance.
          const ctx = (fnErr as any)?.context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json().catch(() => null);
            if (body?.needs_topup) {
              setOffer({
                cost_minor: body.cost_minor,
                currency: body.currency,
                affordable: false,
              });
              setAlreadyUsed(true);
              setError(null);
              setRechargeOpen(true);
              return;
            }
            if (body?.error) throw new Error(body.error);
          }
          throw fnErr;
        }
        if (data?.error) throw new Error(data.error);

        setReading(data.reading as Reading);
        setReadCards((data.cards as DrawnCard[]) ?? cards);
        setAlreadyUsed(!!data.already_used || !!data.free_used);
        setOffer((data.offer as Offer) ?? null);
        if (typeof data.count === "number") setCount(data.count);
    
        // A brand new reading landed, so any cached history list is stale.
        if (data.paid) {
          // The wallet was just debited; the header balance would otherwise keep
          // showing the pre-purchase number until the next page load.
          void refreshUserStatus?.();
        }

        // `already_used` with NO purchase means the server declined to charge and
        // handed back the existing reading instead — the correct, non-billing
        // answer to a request that carried no confirmation. If the spread differs
        // from what was just picked, that has to be said: otherwise they chose
        // three cards, waited, and got three different ones back with no
        // explanation, which reads as a bug.
        if (data.already_used && !data.paid) {
          const returned = (data.cards as DrawnCard[]) ?? cards;
          const samePick =
            returned.length === cards.length &&
            returned.every((c, i) => c.id === cards[i]?.id);
          setReplacedPick(!samePick);
          setPicked(returned);
          setQuestion(data.question || "");
        } else {
          setReplacedPick(false);
        }
      } catch (e: any) {
        setError(
          e?.message?.includes("too long")
            ? "The reading took too long. Please try again."
            : e?.message || "Could not create your reading right now.",
        );
      } finally {
        setLoading(false);
        setPendingBuy(null);
      }
    },
    [refreshUserStatus],
  );

  // A returning user who already spent their free reading must SEE that reading,
  // not a fresh deck. Without this they pick three new cards, hit Read, and get
  // their OLD reading back, which reads like a bug.
  useEffect(() => {
    if (!user || checkedExisting) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("tarot-reading-status", { body: {} });
        if (cancelled || !data) return;
        if (typeof data.count === "number") setCount(data.count);
        if (data.has_reading === false) return;
        if (data.reading) {
          // The LATEST reading, which after a purchase is the paid one — showing
          // the old free reading instead would look like the paid one vanished.
          setReading(data.reading as Reading);
          setReadCards((data.cards as DrawnCard[]) ?? []);
          setPicked((data.cards as DrawnCard[]) ?? []);
          setQuestion(data.question || "");
          setAlreadyUsed(!!data.free_used);
          setOffer((data.offer as Offer) ?? null);
        }
      } catch {
        /* non-fatal: they just see the deck and find out on submit */
      } finally {
        if (!cancelled) setCheckedExisting(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, checkedExisting]);

  // Getting the visitor back to their cards after the Google round trip has to
  // beat four things, and a single scrollIntoView beats none of them:
  //
  //   1. TIMING. On the OAuth return `user` is set from the session immediately
  //      but `currency` is still null, and `visible` reads currency — so this
  //      component renders null for a few hundred ms, and sectionRef is null with
  //      it. A scroll fired then goes nowhere.
  //   2. App.tsx runs window.scrollTo(0, 0) on mount.
  //   3. The browser restores its own scroll position after paint.
  //   4. LAYOUT SHIFT. Images above this section load over the next second and
  //      push it down. This is the one that actually produced the "scrolled for an
  //      instant, then I was looking at the hero again" report.
  //
  // A scroll-event listener was tried and does not work: cause 4 emits no scroll
  // event by definition — layout shifting under a fixed scroll offset is not a
  // scroll — so the one cause most likely to be responsible is the one such a
  // listener structurally cannot see.
  //
  // So the position is re-asserted on a timer instead. rAF gives per-frame
  // precision, which nothing can land between; the interval is a backstop for when
  // rAF is starved (a hidden or non-compositing tab gets zero rAF callbacks, which
  // is exactly what the automation pane this was tested in does). Both call the
  // same idempotent check, so running together is harmless — whichever fires first
  // finds it out of place, fixes it, and the other finds nothing to do.
  //
  // Placement is instant rather than smooth: the visitor has just come back from
  // Google, an anchor-style jump is what they expect there, and a smooth animation
  // would be fighting a per-frame correction the whole way down.
  //
  // It releases the moment the visitor scrolls themselves. Fighting someone who is
  // deliberately scrolling away is far worse than never scrolling at all.
  useEffect(() => {
    if (!scrollWanted || !visible) return;

    let raf = 0;
    let released = false;
    let arrivedAt = 0;
    let waited = 0; // time with no section on screen, ticked by the interval

    const HOLD_MS = 2500; // outlasts restoration and above-the-fold image reflow
    const GIVE_UP_MS = 5000; // section never appeared; stop looking
    // Every re-placement restarts HOLD_MS, so something competing forever (an
    // auto-advancing carousel, a sticky element remeasuring) would keep this alive
    // indefinitely. This is the ceiling that cannot be reset by anything.
    const MAX_MS = 8000;
    const bornAt = performance.now();
    const expired = () => performance.now() - bornAt > MAX_MS;

    const release = () => {
      if (released) return;
      released = true;
      cancelAnimationFrame(raf);
      window.clearInterval(iv);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchstart", release);
      window.removeEventListener("keydown", release);
      setScrollWanted(false);
    };

    // Idempotent: puts the section at the top if it is not already there.
    // Returns false while the section has not mounted yet (cause 1).
    const place = (): boolean => {
      const el = sectionRef.current;
      if (!el) return false;
      const top = el.getBoundingClientRect().top;
      // 8px of slack so sub-pixel rounding does not re-scroll every single frame.
      if (Math.abs(top) >= 8) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        // Anything that moved us restarts the hold, so a late image load gets a
        // full settling window rather than the remainder of the old one.
        arrivedAt = performance.now();
      } else if (!arrivedAt) {
        arrivedAt = performance.now();
      }
      return true;
    };

    const done = () => arrivedAt > 0 && performance.now() - arrivedAt > HOLD_MS;

    const tick = () => {
      if (released) return;
      place();
      if (done() || expired()) return release();
      raf = requestAnimationFrame(tick);
    };

    const iv = window.setInterval(() => {
      if (released) return;
      if (!place()) {
        waited += 100;
        if (waited > GIVE_UP_MS) release();
        return;
      }
      if (done() || expired()) release();
    }, 100);

    raf = requestAnimationFrame(tick);
    window.addEventListener("wheel", release, { passive: true });
    window.addEventListener("touchstart", release, { passive: true });
    window.addEventListener("keydown", release, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(iv);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchstart", release);
      window.removeEventListener("keydown", release);
    };
  }, [scrollWanted, visible]);

  // Price of a further reading, same query AuthContext uses for chat cost.
  useEffect(() => {
    if (!user || !currency) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("service_prices")
          .select("price_amount")
          .eq("currency_code", currency)
          .eq("variant_name", pricingVariant || "control")
          .eq("service_key", "chat_message")
          .maybeSingle();
        if (!cancelled && data?.price_amount) setChatPrice(Number(data.price_amount));
      } catch {
        /* price line is simply omitted if this fails */
      }
    })();
    return () => { cancelled = true; };
  }, [user, currency, pricingVariant]);

  useEffect(() => {
    if (!loading) {
      setLoadStep(0);
      return;
    }
    const t = setInterval(
      () => setLoadStep((n) => Math.min(n + 1, LOADING_STEPS.length - 1)),
      2600,
    );
    return () => clearInterval(t);
  }, [loading]);

  // Resume a draw parked before login. Runs once, and takePendingDraw() clears
  // storage as it reads, so a remount cannot spend the free reading twice.
  useEffect(() => {
    if (!user || resumedRef.current) return;
    const pending = takePendingDraw();
    if (!pending) return;
    resumedRef.current = true;
    setPicked(pending.cards);
    setQuestion(pending.question);
    setDrawId(pending.drawId);

    // Google OAuth is a FULL PAGE RELOAD back to window.location.origin, and
    // App.tsx runs window.scrollTo(0,0) on mount, so the user lands on the hero
    // while the reading generates far below. Ask for a scroll; the effect below
    // performs it once the section actually exists.
    setScrollWanted(true);

    void submit(pending.cards, pending.question, false, pending.drawId);
  }, [user, submit]);

  // Computed outside a functional updater on purpose. Minting the draw id is a
  // side effect, and React StrictMode invokes updaters twice — running it in
  // there would mint two ids for one spread.
  const toggleCard = (index: number) => {
    if (reading || loading) return;
    const card = TAROT_DECK[index];

    const existing = picked.findIndex((p) => p.id === card.id);
    if (existing >= 0) {
      // Taking a card back makes this a different draw, so the id it had is
      // spent. A fresh one is minted when the spread completes again.
      setPicked(picked.filter((_, i) => i !== existing));
      setDrawId(null);
      return;
    }
    if (picked.length >= 3) return;

    // Orientation is decided at pick time and never re-rolled, so the card
    // the visitor chose is the card they get back after signing in.
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const next = [...picked, { id: card.id, reversed: buf[0] % 2 === 0 }];
    setPicked(next);
    if (next.length === 3) setDrawId(newDrawId());
  };

  // Price of another reading. The server's offer wins — it knows about
  // membership discounts and pricing variants — and the service_prices lookup is
  // only the fallback for a first paint where no offer has arrived yet.
  const priceLabel =
    offer != null
      ? fmtPrice(minorToMajor(offer.cost_minor), offer.currency)
      : chatPrice != null
        ? fmtPrice(chatPrice, currency)
        : null;


  const handleRead = () => {
    if (picked.length !== 3) return;
    // The id travels WITH the parked draw. Coming back from Google is a retry of
    // this same draw, so it has to arrive carrying the same id.
    const id = drawId ?? newDrawId();
    if (!drawId) setDrawId(id);

    if (!user) {
      savePendingDraw(picked, question, id);
      onRequestLogin();
      return;
    }
    // Free reading still available: straight through, nothing to confirm.
    if (!alreadyUsed) {
      void submit(picked, question, false, id);
      return;
    }
    // Otherwise this costs money, so ask first. The server would refuse to
    // charge an unconfirmed request anyway; this is the half the user can see.
    if (offer && !offer.affordable) {
      setRechargeOpen(true);
      return;
    }
    setPendingBuy(picked);
  };

  const confirmBuy = () => {
    if (!pendingBuy) return;
    const cards = pendingBuy;
    // Same id the spread already has. A retry after a failure re-sends it and
    // the server hands back the reading it already made instead of charging
    // twice — which is the entire reason the id is minted at pick time rather
    // than here.
    const id = drawId ?? newDrawId();
    if (!drawId) setDrawId(id);
    setPendingBuy(null);
    void submit(cards, question, true, id);
  };

  // Clears the SPREAD, not the history. The previous reading is still on the
  // server and still one tap away below, which is the whole reason buying
  // another is safe to do.
  const drawAgain = () => {
    clearPendingDraw();
    setPicked([]);
    setQuestion("");
    setReading(null);
    setReadCards(null);
    setReplacedPick(false);
    setError(null);
    // A genuinely new draw, so it must not reuse the previous id — the server
    // would recognise it, return the old reading, and the visitor would have
    // picked three new cards for nothing.
    setDrawId(null);
  };

  // "Shuffle again" — clears the spread only. `alreadyUsed` is deliberately NOT
  // cleared: whether the free reading is spent is server truth, and resetting it
  // here would show a "free" button that the server then declines to honour.
  const reset = () => {
    clearPendingDraw();
    resumedRef.current = false;
    setPicked([]);
    setQuestion("");
    setReading(null);
    setReadCards(null);
    setReplacedPick(false);
    setError(null);
    setDrawId(null); // shuffling is a new draw
  };

  if (!visible) return null;

  const shown = readCards ?? picked;

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="tarot-heading">
      <div className={styles.inner}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>
            <IconStar size={13} /> Free tarot reading
          </span>
          <h2 id="tarot-heading" className={styles.h2}>
            Pick three cards. See what they say.
          </h2>
          <p className={styles.sub}>
            One card for your past, one for your present, one for what is coming.
            Choose any three from the deck below
          </p>
        </div>

        {!reading && (
          <div className={styles.askWrap}>
            <label className={styles.askLabel} htmlFor="tarot-question">
              What is on your mind? <span className={styles.askOpt}>optional</span>
            </label>
            <input
              id="tarot-question"
              className={styles.ask}
              value={question}
              onChange={(e) => setQuestion(e.target.value.slice(0, 200))}
              placeholder="e.g. Should I take the new role?"
              disabled={loading}
              autoComplete="off"
            />
          </div>
        )}

        {/* The three slots: face-down until picked, then flipped with the free
            meaning. Hidden once the reading exists, because the reading below
            shows the same three cards WITH their full text. Leaving both up
            printed every card name twice on one screen. */}
        {!reading && (
        <div className={styles.slots}>
          {POSITIONS.map((pos, i) => {
            const drawn = shown[i];
            const card = drawn ? CARD_BY_ID[drawn.id] : null;
            return (
              <div
                key={pos}
                className={`${styles.slot} ${card ? styles.slotFilled : ""}`}
              >
                <span className={styles.slotLabel}>{pos}</span>
                {card ? (
                  <span className={styles.face}>
                    {/* Real Rider-Waite-Smith art (1909, public domain). Only
                        the three drawn cards load an image; the 78 face-down
                        cards stay CSS, so a reading costs ~3 images, not 78.
                        width/height are set so the flip cannot shift layout. */}
                    <img
                      className={`${styles.faceArt} ${
                        drawn.reversed ? styles.faceArtReversed : ""
                      }`}
                      src={`/tarot/${card.id}.webp`}
                      alt={
                        drawn.reversed
                          ? `${card.name}, reversed`
                          : card.name
                      }
                      width={250}
                      height={420}
                      loading="lazy"
                      decoding="async"
                    />
                    <span>
                      <span className={styles.faceName}>{card.name}</span>
                      {drawn.reversed && (
                        <>
                          {" "}
                          <span className={styles.reversedTag}>Reversed</span>
                        </>
                      )}
                      <span className={styles.faceMeaning}>
                        <br />
                        {drawn.reversed ? card.reversed : card.upright}
                      </span>
                    </span>
                  </span>
                ) : (
                  <span className={styles.slotHint}>Tap a card below</span>
                )}
              </div>
            );
          })}
        </div>
        )}

        {!reading && (
          <>
            <p className={styles.counter}>
              <span className={styles.counterStrong}>{picked.length}</span> of 3 chosen
              {picked.length === 3 ? ", ready" : ""}
            </p>

            {/* Two rows in ONE scroller so they pan together; separate
                scrollers would desync. Cards overlap ~55% (see the CSS), so the
                visible sliver is what you tap — like picking from a real spread. */}
            <div className={styles.deckWrap} role="listbox" aria-label="Tarot deck, 78 cards">
              <div className={styles.deckRows}>
                {Array.from({ length: deckRowCount }, (_, rowIdx) => (
                  <div className={styles.deckRow} key={rowIdx}>
                    {TAROT_DECK.slice(rowIdx * perRow, rowIdx * perRow + perRow).map((card, j) => {
                      const i = rowIdx * perRow + j;
                      const isPicked = picked.some((p) => p.id === card.id);
                      const full = picked.length >= 3 && !isPicked;
                      return (
                        <button
                          key={card.id}
                          type="button"
                          role="option"
                          aria-selected={isPicked}
                          aria-label={isPicked ? `Card ${i + 1}, chosen` : `Card ${i + 1}`}
                          disabled={full || loading}
                          onClick={() => toggleCard(i)}
                          className={`${styles.cardBtn} ${isPicked ? styles.chosen : ""} ${
                            full ? styles.dimmed : ""
                          }`}
                        >
                          <span className={styles.back}>
                            <IconSparkles size={20} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {error && <p className={styles.error}>{error}</p>}

        {loading && (
          <div className={styles.loading}>
            {/* Three cards turning, one per position, instead of a bare spinner.
                The call runs ~10-30s (a thinking model), which is far too long
                for a dot to be the only feedback. */}
            <div className={styles.loadCards} aria-hidden="true">
              {POSITIONS.map((p, i) => (
                <span key={p} className={styles.loadCard} style={{ animationDelay: `${i * 0.22}s` }}>
                  <IconSparkles size={18} />
                </span>
              ))}
            </div>
            <p className={styles.loadTitle}>{LOADING_STEPS[loadStep]}</p>
            <p className={styles.loadSub}>This takes a few seconds. The reading is written for your exact spread.</p>
          </div>
        )}

        {!reading && !loading && !pendingBuy && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              disabled={picked.length !== 3}
              onClick={handleRead}
            >
              {picked.length !== 3
                ? `Pick ${3 - picked.length} more card${3 - picked.length === 1 ? "" : "s"}`
                : alreadyUsed
                  ? priceLabel
                    ? `Get this reading · ${priceLabel}`
                    : "Get this reading"
                  : "Get free reading"}
            </button>
            {picked.length > 0 && (
              <button type="button" className={styles.ghost} onClick={reset}>
                <IconRefresh size={16} /> Shuffle again
              </button>
            )}
          </div>
        )}

        {/* Explicit consent before any spend. The server refuses to charge a
            request without confirm_paid, so this is not decoration — it is the
            visible half of the same rule, and it is where the promise that the
            earlier readings survive gets made. */}
        {pendingBuy && !loading && (
          <div className={styles.confirm}>
            <p className={styles.confirmTitle}>
              {priceLabel ? `Unlock this reading for ${priceLabel}?` : "Unlock this reading?"}
            </p>
            <p className={styles.confirmBody}>
              This comes out of your wallet balance.
              {count > 0 && " Your earlier readings stay saved, and this one is added to them."}
            </p>
            <div className={styles.actions} style={{ marginTop: 0 }}>
              <button type="button" className={styles.primary} onClick={confirmBuy}>
                {priceLabel ? `Yes, unlock for ${priceLabel}` : "Yes, unlock"}
              </button>
              <button type="button" className={styles.ghost} onClick={() => setPendingBuy(null)}>
                Not now
              </button>
            </div>
          </div>
        )}

        {reading && (
          <>
            {replacedPick && (
              <p className={styles.staleNote}>
                Your free reading is already used, so this is the one you
                received before, with the cards you drew then. The cards you just
                picked are still waiting, so you can unlock them
                {priceLabel ? ` for ${priceLabel}` : ""} below.
              </p>
            )}

            <div className={styles.reading}>
              {POSITIONS.map((pos, i) => {
                const drawn = shown[i];
                const card = drawn ? CARD_BY_ID[drawn.id] : null;
                const body = [reading.past, reading.present, reading.future][i];
                return (
                  <div key={pos} className={styles.readCard} style={{ animationDelay: `${i * 0.12}s` }}>
                    {card && (
                      <img
                        className={`${styles.readArt} ${
                          drawn?.reversed ? styles.faceArtReversed : ""
                        }`}
                        src={`/tarot/${card.id}.webp`}
                        alt={drawn?.reversed ? `${card.name}, reversed` : card.name}
                        width={250}
                        height={420}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <div className={styles.readHead}>
                      <span className={styles.readPos}>{pos}</span>
                      {drawn?.reversed && (
                        <span className={styles.reversedTag}>Reversed</span>
                      )}
                    </div>
                    <p className={styles.readCardName}>{card?.name}</p>
                    <p className={styles.readBody}>{body}</p>
                  </div>
                );
              })}
              {reading.summary && (
                <div className={`${styles.readCard} ${styles.summary}`} style={{ animationDelay: "0.36s" }}>
                  <div className={styles.readHead}>
                    <span className={styles.readPos}>Together</span>
                  </div>
                  <p className={styles.readBody}>{reading.summary}</p>
                </div>
              )}
            </div>

            {/* Another reading, ABOVE the chat upsell.

                Order is the whole point here. Someone who has just finished a
                tarot reading is in the market for tarot, so the offer that
                matches what they are already doing goes first; the chart pitch
                asks them to switch products and belongs after it.

                The offer is only half the job though: the reassurance next to it
                is the other half, because someone who thinks a new reading
                REPLACES this one will not buy it. */}
            {alreadyUsed && (
              <div className={styles.again}>
                <p className={styles.againTitle}>Want another reading?</p>
                <p className={styles.againBody}>
                  {priceLabel
                    ? `Draw three new cards for ${priceLabel}. This reading stays saved, so you can come back to it any time.`
                    : "Draw three new cards. This reading stays saved, so you can come back to it any time."}
                </p>
                <div className={styles.actions} style={{ marginTop: 0 }}>
                  <button type="button" className={styles.secondary} onClick={drawAgain}>
                    <IconRefresh size={16} style={{ verticalAlign: "-3px" }} /> Draw new cards
                  </button>
                </div>
              </div>
            )}

            <div className={styles.upsell}>
              <p className={styles.upsellTitle}>
                Know about your future through planetary positions
              </p>
              <p className={styles.upsellBody}>
                Tarot shows this moment. Your birth chart shows the timing: which
                planetary periods are running now, and when things actually turn.
                Ask Aura AI and get answers.
              </p>
              <div className={styles.actions} style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => navigate("/chat")}
                >
                  <IconMessageCircle2 size={18} style={{ verticalAlign: "-3px" }} /> Know more
                </button>
              </div>
            </div>
          </>
        )}

        {/* Past readings. The proof, not just the promise: a visitor who can SEE
            the earlier readings listed knows buying another adds to them. Loaded
            lazily — most visitors never open it. */}
        {/* Past readings live on their own screen.

            A reading is four paragraphs, so an inline accordion here grows the
            homepage by a screenful for every reading bought — the exact clutter
            a dedicated screen exists to absorb. What stays is the one thing this
            spot has to say: they are saved, and here is where they are. */}
        {user && count >= 1 && !loading && (
          <div className={styles.history}>
            <button
              type="button"
              className={styles.historyToggle}
              onClick={() => navigate("/tarot-readings")}
            >
              <IconHistory size={16} />
              Past readings ({count})
              <IconChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Short on balance. Reuses the sheet chat and reports already use, so
            the top-up contract stays in exactly one place, and retries the
            purchase the moment it succeeds. */}
        <RechargeSheet
          isOpen={rechargeOpen}
          onClose={() => setRechargeOpen(false)}
          title="Add balance for another reading"
          subtitle={
            priceLabel
              ? `Another tarot reading costs ${priceLabel}.`
              : "You need a little more balance for another reading."
          }
          source="tarot_reading"
          onRecharged={() => {
            setRechargeOpen(false);
            void refreshUserStatus?.();
            // They topped up to buy THIS spread, so put them back on the
            // confirmation rather than making them find the button again.
            if (picked.length === 3) setPendingBuy(picked);
          }}
        />
      </div>
    </section>
  );
}
